import { z } from 'zod';
import {
  CONTRIBUINTES_ICMS,
  TIPOS_ENDERECO,
  documentoValido,
  type DadosParceiroResponse,
} from '@triade/contracts';
import type { ParceiroCampos } from './parceiro-blocos';

/**
 * Formulario do parceiro (Clientes e Fornecedores). So converte texto de tela em DTO: vazio vira ausente (criar) ou null (atualizar).
 * Nenhum calculo: preco, custo, margem, imposto e arredondamento nao existem aqui.
 */

const texto = (valor: string): string | undefined => {
  const t = valor.trim();
  return t === '' ? undefined : t;
};

/** Esquema do formulario. `mensagemNome` muda conforme o papel (cliente ou fornecedor). */
export function schemaFormParceiro(mensagemNome: string) {
  return z.object({
    natureza: z.enum(['FISICA', 'JURIDICA']),
    nome: z.string().trim().min(1, mensagemNome).max(200, 'O nome tem no máximo 200 caracteres'),
    fantasia: z.string().max(200, 'Até 200 caracteres'),
    /** So digitos (11 ou 14) com digito verificador; vazio = nao informado. */
    documento: z
      .string()
      .refine((v) => v === '' || v.length === 11 || v.length === 14, 'Informe 11 dígitos (CPF) ou 14 (CNPJ)')
      .refine((v) => v === '' || documentoValido(v), 'CPF ou CNPJ inválido (confira os dígitos)'),
    ie: z.string().max(30, 'Até 30 caracteres'),
    contribuinteIcms: z.enum(['', ...CONTRIBUINTES_ICMS]),
    observacoes: z.string().max(2000, 'Até 2000 caracteres'),
    enderecos: z.array(
      z.object({
        tipo: z.enum(TIPOS_ENDERECO),
        principal: z.boolean(),
        cep: z.string(),
        logradouro: z.string(),
        numero: z.string(),
        complemento: z.string(),
        bairro: z.string(),
        municipio: z.string(),
        uf: z.string(),
        codigoIbge: z.string(),
      }),
    ),
    contatos: z.array(
      z.object({
        nome: z.string(),
        cargo: z.string(),
        email: z.string(),
        telefone: z.string(),
        /** Nao aparece na tela de contato, mas vem da API e precisa voltar na gravacao (nao se perde ao salvar). */
        observacao: z.string().optional(),
      }),
    ),
  });
}

export type ParceiroFormValores = z.infer<ReturnType<typeof schemaFormParceiro>>;

/** Valores de um cadastro existente (resposta da API) para o formulario. */
export function valoresDoParceiro(r: DadosParceiroResponse & { nome: string; documento: string | null }): ParceiroFormValores {
  return {
    natureza: r.natureza,
    nome: r.nome,
    fantasia: r.fantasia ?? '',
    documento: r.documento ?? '',
    ie: r.ie ?? '',
    contribuinteIcms: r.contribuinteIcms ?? '',
    observacoes: r.observacoes ?? '',
    enderecos: r.enderecos.map((e) => ({
      tipo: e.tipo,
      principal: e.principal,
      cep: e.cep ?? '',
      logradouro: e.logradouro ?? '',
      numero: e.numero ?? '',
      complemento: e.complemento ?? '',
      bairro: e.bairro ?? '',
      municipio: e.municipio ?? '',
      uf: e.uf ?? '',
      codigoIbge: e.codigoIbge ?? '',
    })),
    contatos: r.contatos.map((c) => ({
      nome: c.nome,
      cargo: c.cargo ?? '',
      email: c.email ?? '',
      telefone: c.telefone ?? '',
      observacao: c.observacao ?? '',
    })),
  };
}

function paraEnderecos(lista: ParceiroFormValores['enderecos']) {
  return lista.map((e) => ({
    tipo: e.tipo,
    principal: e.principal,
    cep: texto(e.cep),
    logradouro: texto(e.logradouro),
    numero: texto(e.numero),
    complemento: texto(e.complemento),
    bairro: texto(e.bairro),
    municipio: texto(e.municipio),
    codigoIbge: texto(e.codigoIbge),
    uf: texto(e.uf),
  }));
}

function paraContatos(lista: ParceiroFormValores['contatos']) {
  return lista.map((c) => ({
    nome: c.nome.trim(),
    cargo: texto(c.cargo),
    email: texto(c.email),
    telefone: texto(c.telefone),
    observacao: texto(c.observacao ?? ''),
  }));
}

/** Corpo de criacao (campos vazios ficam fora do corpo). */
export function dadosParceiroCriar(v: ParceiroFormValores) {
  return {
    natureza: v.natureza,
    nome: v.nome.trim(),
    documento: texto(v.documento),
    fantasia: texto(v.fantasia),
    ie: texto(v.ie),
    contribuinteIcms: v.contribuinteIcms === '' ? undefined : v.contribuinteIcms,
    observacoes: texto(v.observacoes),
    ...(v.enderecos.length > 0 ? { enderecos: paraEnderecos(v.enderecos) } : {}),
    ...(v.contatos.length > 0 ? { contatos: paraContatos(v.contatos) } : {}),
  };
}

/** Corpo de atualizacao: campo vazio vai como null (limpa); enderecos e contatos vao inteiros. */
export function dadosParceiroAtualizar(v: ParceiroFormValores) {
  return {
    natureza: v.natureza,
    nome: v.nome.trim(),
    documento: texto(v.documento) ?? null,
    fantasia: texto(v.fantasia) ?? null,
    ie: texto(v.ie) ?? null,
    contribuinteIcms: v.contribuinteIcms === '' ? null : v.contribuinteIcms,
    observacoes: texto(v.observacoes) ?? null,
    enderecos: paraEnderecos(v.enderecos),
    contatos: paraContatos(v.contatos),
  };
}

/** Valores iniciais de um cadastro novo (natureza juridica por padrao, como no componente). */
export function valoresNovoParceiro(): ParceiroFormValores {
  const inicial: ParceiroCampos = { natureza: 'JURIDICA', nome: '', fantasia: '', documento: '', ie: '', contribuinteIcms: '', observacoes: '', enderecos: [], contatos: [] };
  return inicial;
}
